import { type FC, useMemo, useState } from "react";
import { useApi } from "../../api/APIContext";
import type { Exam, ExamParticipant } from "../../api/dto";
import { countryOptions } from "../../util/countries";
import { Autocomplete } from "../form/Autocomplete";
import { Input } from "../form/Input";
import { SuggestionInput } from "../form/SuggestionInput";

export const maxParticipantPosition = 24;
const noSelection = "-";
const otherCertification = "Anders";

export interface ParticipantFormValues {
  name: string;
  contractor: string;
  position: number | null;
  subContractor: string;
  /** nationality is a country code, or "" when not set. */
  nationality: string;
  /** language is a country code, or "" when not set. */
  language: string;
  badgeNumber: string;
  certification: string;
}

export function participantFormValues(
  participant?: ExamParticipant,
  position: number | null = null,
): ParticipantFormValues {
  return {
    name: participant?.name ?? "",
    contractor: participant?.contractor ?? "",
    position: participant?.position ?? position,
    subContractor: participant?.subContractor ?? "",
    nationality: participant?.nationality ?? "",
    language: participant?.language ?? "",
    badgeNumber: participant?.badgeNumber ?? "",
    certification: participant?.certification ?? "",
  };
}

export function isParticipantFormValid(values: ParticipantFormValues): boolean {
  return values.name.trim() !== "" && values.contractor.trim() !== "" && values.position !== null;
}

/** firstFreePosition returns the lowest position that is not used by one of the participants. */
export function firstFreePosition(participants: ExamParticipant[]): number | null {
  const taken = new Set(participants.filter((p) => !p.deletedAt).map((p) => p.position));
  for (let position = 1; position <= maxParticipantPosition; position++) {
    if (!taken.has(position)) return position;
  }
  return null;
}

/** participantFormPayload converts the form to the fields the api expects. */
export function participantFormPayload(values: ParticipantFormValues) {
  // Certificate types can contain %C, which stands for the country code of the language of the participant.
  const certification =
    values.certification.includes("%C") && values.language !== ""
      ? values.certification.replaceAll("%C", values.language)
      : values.certification;
  return {
    name: values.name.trim(),
    contractor: values.contractor.trim(),
    position: values.position,
    subcontractor: values.subContractor.trim() || null,
    nationality: values.nationality || null,
    language: values.language || null,
    badge_number: values.badgeNumber.trim() || null,
    certification: certification.trim() || null,
  };
}

/** initials returns the first letters of the first two words of a name, e.g. "Piet Dekker" -> "PD". */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");
}

/** matchingCertificateType returns the certificate type that matches the certification, taking %C into account. */
function matchingCertificateType(certification: string, names: string[]): string | null {
  for (const name of names) {
    if (name.includes("%C")) {
      const escaped = name.split("%C").map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
      if (new RegExp(`^${escaped.join(".*")}$`).test(certification)) return name;
    } else if (name === certification) {
      return name;
    }
  }
  return null;
}

export const ParticipantForm: FC<{
  exam: Exam;
  participants: ExamParticipant[];
  /** editing is the participant that is being edited, its position stays selectable. */
  editing?: ExamParticipant;
  values: ParticipantFormValues;
  onChange: (values: ParticipantFormValues) => void;
}> = ({ exam, participants, editing, values, onChange }) => {
  const { certificateTypes, exams, participants: allParticipants } = useApi();
  const countries = useMemo(() => countryOptions(), []);
  const countryChoices = useMemo(() => ({ [noSelection]: "Geen", ...countries }), [countries]);
  const set = <K extends keyof ParticipantFormValues>(key: K, value: ParticipantFormValues[K]) =>
    onChange({ ...values, [key]: value });

  // Contractors and subcontractors that already exist within the company of this exam.
  const { contractorSuggestions, subContractorSuggestions } = useMemo(() => {
    const companyExamIds = new Set(
      exams.filter((e) => e.companyId === exam.companyId).map((e) => e.id),
    );
    const companyParticipants = allParticipants.filter((p) => companyExamIds.has(p.examId));
    const uniqueSorted = (names: (string | null | undefined)[]) =>
      [...new Set(names.map((name) => name?.trim()).filter((name): name is string => !!name))].sort(
        (a, b) => a.localeCompare(b),
      );
    return {
      contractorSuggestions: uniqueSorted(companyParticipants.map((p) => p.contractor)),
      subContractorSuggestions: uniqueSorted(companyParticipants.map((p) => p.subContractor)),
    };
  }, [exams, allParticipants, exam.companyId]);

  const takenPositions = useMemo(() => {
    const taken = new Map<number, ExamParticipant>();
    for (const participant of participants) {
      if (participant.deletedAt || participant.id === editing?.id) continue;
      taken.set(participant.position, participant);
    }
    return taken;
  }, [participants, editing]);

  const certificateTypeNames = useMemo(
    () =>
      certificateTypes
        .filter((type) => type.question_set_id === exam.questionSetId && type.is_active)
        .map((type) => type.name)
        .sort((a, b) => a.localeCompare(b)),
    [certificateTypes, exam.questionSetId],
  );
  const matchedCertificateType = matchingCertificateType(
    values.certification,
    certificateTypeNames,
  );
  // "Anders" lets the user type a custom certification, which starts out empty so it can't be derived from the value.
  const [otherSelected, setOtherSelected] = useState(
    values.certification !== "" && matchedCertificateType === null,
  );
  const certificationChoice = otherSelected
    ? otherCertification
    : values.certification === ""
      ? noSelection
      : (matchedCertificateType ?? otherCertification);
  const showCertificationText =
    certificateTypeNames.length === 0 || certificationChoice === otherCertification;

  return (
    <div className={"space-y-3"}>
      <Input
        type={"text"}
        label={"Naam"}
        value={values.name}
        onChange={(name) => set("name", name)}
        maxWidth={"full"}
        focus
      />

      <div>
        <div className={"text-sm font-medium text-brand-900"}>Positie</div>
        {/* Two even rows of 12 with evenly sized buttons that fill the width, 6 per row on small screens. */}
        <div className={"mt-2 grid grid-cols-6 gap-2 sm:grid-cols-12"}>
          {Array.from({ length: maxParticipantPosition }, (_, i) => i + 1).map((position) => {
            const takenBy = takenPositions.get(position);
            const selected = values.position === position;
            return (
              <div key={position} className={"flex flex-col items-center"}>
                <button
                  type={"button"}
                  disabled={takenBy !== undefined}
                  title={takenBy ? `Bezet door ${takenBy.name}` : undefined}
                  onClick={() => set("position", position)}
                  className={`h-9 w-full rounded-md text-sm ${
                    selected
                      ? "bg-brand-800 text-white"
                      : takenBy
                        ? "bg-slate-100 text-slate-300 cursor-not-allowed"
                        : "bg-slate-100 text-slate-700 hover:bg-brand-100"
                  }`}
                >
                  {position}
                </button>
                {/* Initials of the participant that holds the position, the space is always reserved to keep the rows even. */}
                <span className={"h-4 text-[10px] leading-4 text-slate-400"}>
                  {takenBy ? initials(takenBy.name) : ""}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <SuggestionInput
        label={"Contractor"}
        value={values.contractor}
        onChange={(contractor) => set("contractor", contractor)}
        suggestions={contractorSuggestions}
        newValueHint={"Nieuwe contractor"}
      />

      <SuggestionInput
        label={"Subcontractor (optioneel)"}
        value={values.subContractor}
        onChange={(subContractor) => set("subContractor", subContractor)}
        suggestions={subContractorSuggestions}
        newValueHint={"Nieuwe subcontractor"}
      />

      <div className={"grid grid-cols-1 sm:grid-cols-2 gap-3"}>
        <Autocomplete
          label={"Nationaliteit (optioneel)"}
          options={countryChoices}
          value={values.nationality === "" ? noSelection : values.nationality}
          onChange={(nationality) =>
            set("nationality", nationality === noSelection ? "" : nationality)
          }
        />
        <Autocomplete
          label={"Taal (optioneel)"}
          options={countryChoices}
          value={values.language === "" ? noSelection : values.language}
          onChange={(language) => set("language", language === noSelection ? "" : language)}
        />
      </div>
      <Input
        type={"text"}
        label={"Badge nummer (optioneel)"}
        value={values.badgeNumber}
        onChange={(badgeNumber) => set("badgeNumber", badgeNumber)}
        maxWidth={"full"}
      />
      {certificateTypeNames.length > 0 && (
        <Autocomplete
          label={"Certificering (optioneel)"}
          options={{
            [noSelection]: "Selecteer certificaat",
            ...Object.fromEntries(certificateTypeNames.map((name) => [name, name])),
            [otherCertification]: "Anders",
          }}
          value={certificationChoice}
          onChange={(choice) => {
            setOtherSelected(choice === otherCertification);
            set(
              "certification",
              choice === noSelection || choice === otherCertification ? "" : choice,
            );
          }}
        />
      )}
      {showCertificationText && (
        <Input
          type={"text"}
          label={"Certificering (optioneel)"}
          value={values.certification}
          onChange={(certification) => set("certification", certification)}
          maxWidth={"full"}
        />
      )}
    </div>
  );
};
